import { FinanceLabFieldType } from "@/entities/FinanceLab/FinanceLab.model";
import { DeltaFormula } from "../InvestmentMemo.types";
import { getPeriodRange } from "./periods";
import { formatFinanceLabValue } from "@/entities/FinanceLab/FinanceLab.format";

const interleave = (arr: string[], sep: string) =>
    arr.flatMap((v, i) => (i === 0 ? [v] : [sep, v]));

// Get the sums of all periods inthe previous and current windows
export function getSliceSum(slice: [string, number][]): number {
    return slice.reduce((sum, [, val]) => sum + val, 0);
}

// Get the 2 slices of periods based on the period window
export function getPeriodSlices(
    windowSize: number = 1,
    valuesByPeriod: [string, number][],
): {
    previous: [string, number][];
    current: [string, number][];
} | null {
    if (valuesByPeriod.length < windowSize * 2) return null;
    const periods = valuesByPeriod
        .sort((a, b) => a[0].localeCompare(b[0]))
        .slice(-windowSize * 2);
    const previous = periods.slice(0, windowSize);
    const current = periods.slice(windowSize);
    return { previous: previous, current: current };
}

// Get formula for a given slice
export function getFormula(
    slice: [string, number][],
    fieldType: FinanceLabFieldType = FinanceLabFieldType.CURRENCY,
): DeltaFormula {
    const values = slice.map(([, val]) =>
        formatFinanceLabValue(val, fieldType),
    );

    return {
        title: getPeriodRange(slice.map(([period]) => period)),
        formula: interleave(values, "+"),
    };
}

// Get the formula parts for the delta calculation
export function getDeltaFormula(
    previousPeriods: string[],
    currentPeriods: string[],
    resolutionWindow: string,
): DeltaFormula {
    const previous = previousPeriods.length
        ? getPeriodRange(previousPeriods)
        : "";
    const current = currentPeriods.length ? getPeriodRange(currentPeriods) : "";
    return {
        title: getDeltaLabel(resolutionWindow),
        formula: ["(", current, "-", previous, ")", "/", previous],
    };
}

export function calculateDelta(
    currentSlice: [string, number][],
    previousSlice: [string, number][],
): number | null {
    const currentSum = getSliceSum(currentSlice);
    const previousSum = getSliceSum(previousSlice);
    if (previousSum === 0) return null;
    return (currentSum - previousSum) / previousSum;
}

// Accepts window like "l12m", "l4q", "l3y"
export function getDeltaLabel(resolutionWindow: string): string {
    const match = resolutionWindow.match(/^l(\d+)([mqy])$/);
    if (!match) return "% Δ PoP";

    const count = parseInt(match[1], 10);
    const unit = match[2];

    if (unit === "m" && count === 12) return "% Δ YoY";
    if (unit === "m" && count === 3) return "% Δ QoQ";
    if (unit === "q" && count === 4) return "% Δ YoY";

    return "% Δ PoP";
}

export function getDeltaPeriodCount(resolutionWindow: string): number {
    const match = resolutionWindow.match(/^l(\d+)[mqy]$/);
    return match ? parseInt(match[1], 10) : 1;
}
